import React, { useState, useEffect } from 'react';
import { ModalWrapper, ButtonGroup } from 'components/molecules';
import { PopupProps } from 'react-popup-manager/dist/src';
import { Form, Button, Input, Select, Space, Upload, message, Row, Col } from 'antd';
import { isEmpty } from 'utils/utils';
import { useProjectAskNoteDetail } from 'hooks';
import { UploadFile } from 'antd/lib/upload/interface';
import { UploadOutlined, CloseOutlined, PaperClipOutlined, DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import { isFileViewer } from 'utils/file-utils';
import moment from 'moment';
import { DatePicker } from 'components/atoms';

export interface FileInfoType {
    fileid: number, 
    filename: string    
}

export interface ProjectAskNoteEditModalProps extends PopupProps {
    id: number;
    ask_group_srl: number;
    crm: string;
    company: string;    
    onDownload?: (fileSeq:number, fileName: string) => void;
    onFilePreview?: (fileSeq:number, fileName: string, seq: number, gubun: string, editable: boolean) => void;     
}

const ProjectAskNoteEditModal = ({ 
    isOpen, 
    onClose,
    onDownload,
    onFilePreview,
    id, ask_group_srl, crm, company}: ProjectAskNoteEditModalProps): React.ReactElement => {
  /* ======================================================
   * states
   * ====================================================== */    
    const [isFormDone, setIsFormDone] = useState<boolean>(false);
    const [noteFileList, setNoteFileList] = useState<UploadFile[]>([]);
    const [noteFile, setNoteFile] = useState<FileInfoType>({fileid:0, filename:''});  

  /* ======================================================
  * hooks
  * ====================================================== */
  const { noteInfo, insertSuccess, updateSuccess, noteInsert, noteUpdate } = useProjectAskNoteDetail(id, ask_group_srl);

   /* ======================================================
   * variables
   * ====================================================== */
  const [form] = Form.useForm();
  const { TextArea } = Input;
   /* ======================================================
   * events
   * ====================================================== */
    const onCancel = () => {
        onClose?.(false);
    };

    const onSubmit = (e) => {
        const pdata = 
        {
            id: id,
            ask_group_srl: ask_group_srl,
            metting_type: e.metting_type, 
            metting_date: e.metting_date,
            title: e.title,
            memo: e.memo,
            propose_url: e.propose_url,
            metting_note_file_id: noteFile.fileid,
            metting_note_fileData: noteFileList.map((file) => file.originFileObj),
        }

        if (id > 0) {
            noteUpdate(pdata)
        } else {
            noteInsert(pdata)
        }
    };

    const onChangeForm = () => { 
        setIsFormDone(
            !(
                isEmpty(form.getFieldValue('metting_type')) || 
                form.getFieldValue('metting_date') === 'Invalid date' || 
                isEmpty(form.getFieldValue('memo')) || 
                isEmpty(form.getFieldValue('title')) 
            )
        );    

    };      


  /* ======================================================
   * effects
   * ====================================================== */
    useEffect(() => {
        if (!!noteInfo) {            
            form.setFieldsValue({
                metting_type: noteInfo.metting_type, 
                metting_date: noteInfo.metting_date,
                title: noteInfo.title,
                memo: noteInfo.memo,
                propose_url: noteInfo.propose_url,
            });

            setNoteFile({fileid:noteInfo.metting_note_file_id, filename:noteInfo.metting_note_file_name});
        }
    }, [noteInfo])

    useEffect(() => {
        if (insertSuccess || updateSuccess) {
            onClose?.(true);  
        }
    }, [insertSuccess, updateSuccess])

    return (
    <>
        <ModalWrapper open={isOpen} onCancel={onCancel} width={750} title={id===0?'미팅노트 등록':'미팅노트 수정'} closable>
            <Form
                form={form}
                name="ProjectInfo"
                layout="horizontal"  
                labelCol={{ flex: '120px' }}
                onFieldsChange={onChangeForm}
                onFinish={onSubmit}
                style={{width:'100%'}}
                autoComplete="off"
                initialValues={{metting_date: moment().format('YYYY-MM-DD')}}
            >
                <Row>
                    <Col span={9}>
                    <Form.Item label={'고객사'}>
                        <label>{company}</label>                  
                    </Form.Item> 
                    </Col> 
                    <Col span={15}> 
                    <Form.Item label={'담당자'}>
                        <label>{crm}</label>                  
                    </Form.Item>   
                    </Col>            
                </Row> 
                <Form.Item name="metting_type" label={'미팅형태'} rules={[{ required: true, message: '' }]}>
                    <Select placeholder="선택" options={[{value:'ONLINE', label:'유선미팅'},{value:'OFFLINE', label:'외부미팅'}]} style={{width:"180px"}}/>
                </Form.Item>    
                <Form.Item 
                    name="metting_date" 
                    label={'미팅 일'} 
                    rules={[{ required: true, message: '' }]}
                    getValueProps={(i) => ({value: i!=='Invalid date'? moment(i): ''})}
                    getValueFromEvent={(onChange) => moment(onChange).format('YYYY-MM-DD')}                    
                >
                    <DatePicker 
                        showTime={false}
                        format="YYYY-MM-DD"
                    />  
                </Form.Item>                 
                <Form.Item name='title' label={'미팅주제'} rules={[{ required: true, message: '' }]}>
                    <Input placeholder='주제를 입력하세요'/>   
                </Form.Item>                
                <Form.Item name='memo' label={'미팅노트'} rules={[{ required: true, message: '' }]}>
                    <TextArea rows={3} placeholder='미팅 정보를 입력하세요'/>   
                </Form.Item>                
                <Form.Item name='propose_url' label={'제안 경로'}>
                    <Input placeholder='제안 경로 주소를 입력하세요'/>   
                </Form.Item>   
                <Form.Item name="metting_note_file_id" label={'첨부 파일'}>
                    <Space direction="vertical">
                        <Upload
                            fileList={noteFileList}
                            onChange={({ fileList: list }) => {
                                setNoteFileList(list);
                                onChangeForm();
                            }} 
                            beforeUpload={(file, filelist) => {
                                if ((file.size as number) >= 10 * 1024 * 1024) {
                                  message.warn('파일 용량 초과', 1);
                                  return Upload.LIST_IGNORE;
                                }
                            }}                                                       
                            customRequest={() => true}
                            maxCount={1}
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.hwp,.zip,.txt"
                            showUploadList={false}
                            disabled={noteFile.fileid > 0}
                        >
                            <Button icon={<UploadOutlined />} style={{fontSize:'12px'}}>등록</Button>
                        </Upload>        
                        {(noteFile.fileid > 0) && (
                            <Space key={noteFile.fileid}>
                                <Button onClick={()=>{onDownload && onDownload(noteFile.fileid, noteFile.filename)}} shape="round" icon={<DownloadOutlined />} size='small' style={{padding:'0px 3px'}}/>
                                {isFileViewer(noteFile.filename) && (<Button onClick={()=>{onFilePreview && onFilePreview(noteFile.fileid, noteFile.filename, id, 'note', false)}} shape="round" icon={<SearchOutlined />} size='small' style={{padding:'0px 3px'}}/>)}                                   

                                {noteFile.filename}
                                <CloseOutlined
                                    style={{ fontSize: 12, color: '#b9b9b9' }}
                                    onClick={() => {
                                        setNoteFile({fileid:0, filename:''});
                                        onChangeForm();
                                    }}
                                />
                            </Space>
                        )} 
                        {noteFileList?.map(({ uid, name }) => (
                            <Space key={uid}>
                            <PaperClipOutlined /><span style={{width: '200px'}}>{name}</span>
                            <CloseOutlined
                                style={{ fontSize: 12, color: '#b9b9b9' }}
                                onClick={() => {
                                    setNoteFileList(noteFileList.map((file) => file).filter((file) => file.uid !== uid));
                                    onChangeForm();
                                }}
                            />
                            </Space>
                        ))}                                               
                    </Space>                                        
                </Form.Item>                                                                                                   
                <ButtonGroup className={'buttonsWrapper'}>
                    <Button onClick={onCancel}>취소</Button>
                    <Button htmlType="submit"  type="primary" disabled={!isFormDone}>{id===0?'등록':'수정'}</Button>
                </ButtonGroup>                
            </Form>            
        </ModalWrapper>  
    </>
    )    
};

export default React.memo(ProjectAskNoteEditModal);    